/**
 * WebSerial API wrapper for Safetrax device communication
 */

import { delay } from './utils.js';

export class SerialConnection {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.isConnected = false;
    this.baudRate = 115200;
    this.readBuffer = '';
    this.emitTimeout = null;
    this.listeners = {
      data: [],
      connect: [],
      disconnect: [],
      error: []
    };
  }

  /**
   * Check if WebSerial API is supported
   * @returns {boolean}
   */
  static isSupported() {
    return 'serial' in navigator;
  }

  /**
   * Request port selection from user
   * @returns {Promise<SerialPort|null>}
   */
  async requestPort() {
    try {
      if (!SerialConnection.isSupported()) {
        throw new Error('WebSerial API not supported in this browser');
      }

      this.port = await navigator.serial.requestPort();
      return this.port;
    } catch (err) {
      if (err.name === 'NotFoundError') {
        // User cancelled the dialog
        return null;
      }
      throw err;
    }
  }

  /**
   * Connect to the serial port
   * @param {SerialPort} port - Optional port (uses stored port if not provided)
   * @returns {Promise<boolean>}
   */
  async connect(port = null) {
    try {
      if (port) {
        this.port = port;
      }

      if (!this.port) {
        const selectedPort = await this.requestPort();
        if (!selectedPort) {
          return false;
        }
      }

      // Open port with Safetrax parameters
      await this.port.open({
        baudRate: this.baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none'
      });

      // Set up reader and writer
      this.reader = this.port.readable.getReader();
      this.writer = this.port.writable.getWriter();
      this.isConnected = true;

      // Start reading loop in background
      this.startReading();

      // Emit connect event
      this.emit('connect');

      return true;
    } catch (err) {
      this.emit('error', { message: `Connection failed: ${err.message}`, error: err });
      throw err;
    }
  }

  /**
   * Disconnect from the serial port
   */
  async disconnect() {
    try {
      // Clear any pending emit timeout
      if (this.emitTimeout) {
        clearTimeout(this.emitTimeout);
        this.emitTimeout = null;
      }
      
      // Cancel reader
      if (this.reader) {
        await this.reader.cancel();
        this.reader.releaseLock();
        this.reader = null;
      }

      // Release writer
      if (this.writer) {
        await this.writer.releaseLock();
        this.writer = null;
      }

      // Close port
      if (this.port) {
        await this.port.close();
        this.port = null;
      }

      this.isConnected = false;
      this.readBuffer = '';

      // Emit disconnect event
      this.emit('disconnect');
    } catch (err) {
      this.emit('error', { message: `Disconnect failed: ${err.message}`, error: err });
      throw err;
    }
  }

  /**
   * Start reading data from the port
   */
  async startReading() {
    try {
      while (this.isConnected && this.reader) {
        const { value, done } = await this.reader.read();
        
        if (done) {
          break;
        }

        // Decode data
        const text = new TextDecoder().decode(value);
        this.readBuffer += text;

        // Process complete lines
        const lines = this.readBuffer.split('\n');
        
        // Keep last incomplete line in buffer
        this.readBuffer = lines.pop();

        // Emit each complete line
        for (const line of lines) {
          if (line.trim()) {
            this.emit('data', line.trim());
          }
        }
        
        // IMPORTANT: Also emit any incomplete data immediately for commands
        // that don't send newlines (like V command which sends "V<")
        if (this.readBuffer) {
          // Use a short delay to allow data to accumulate, then emit
          if (this.emitTimeout) {
            clearTimeout(this.emitTimeout);
          }
          this.emitTimeout = setTimeout(() => {
            if (this.readBuffer) {
              this.emit('data', this.readBuffer);
              this.readBuffer = '';
            }
          }, 50); // 50ms delay to allow multi-byte responses to accumulate
        }
      }
    } catch (err) {
      if (err.name !== 'NotReadableError') {
        this.emit('error', { message: `Read error: ${err.message}`, error: err });
      }
      
      // Connection lost
      if (this.isConnected) {
        await this.disconnect();
      }
    }
  }

  /**
   * Write data to the port
   * @param {string|Uint8Array} data - Data to write
   * @returns {Promise<void>}
   */
  async write(data) {
    if (!this.isConnected || !this.writer) {
      throw new Error('Not connected to serial port');
    }

    try {
      const bytes = typeof data === 'string' 
        ? new TextEncoder().encode(data)
        : data;
      
      await this.writer.write(bytes);
    } catch (err) {
      this.emit('error', { message: `Write error: ${err.message}`, error: err });
      throw err;
    }
  }

  /**
   * Write a command (single character)
   * @param {string} command - Single character command
   * @returns {Promise<void>}
   */
  async writeCommand(command) {
    await this.write(command);
    await delay(10); // Small delay between commands
  }

  /**
   * Write bytes to the port
   * @param {Uint8Array} bytes - Bytes to write
   * @returns {Promise<void>}
   */
  async writeBytes(bytes) {
    await this.write(bytes);
  }

  /**
   * Add event listener
   * @param {string} event - Event name (data, connect, disconnect, error)
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event to all listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data = null) {
    if (this.listeners[event]) {
      for (const callback of this.listeners[event]) {
        callback(data);
      }
    }
  }

  /**
   * Clear all event listeners
   */
  removeAllListeners() {
    for (const event in this.listeners) {
      this.listeners[event] = [];
    }
  }

  /**
   * Get list of available ports (only works after user permission)
   * @returns {Promise<SerialPort[]>}
   */
  static async getPorts() {
    if (!SerialConnection.isSupported()) {
      return [];
    }
    return await navigator.serial.getPorts();
  }
}
